import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PenSquare,
  MonitorPlay,
  Image as ImageIcon,
  Layers,
  ClipboardCheck,
  Video,
  AudioLines,
  Workflow,
  LayoutDashboard,
  PieChart,
  ArrowRight,
  ChevronDown,
  Book,
  BookMarked,
} from "lucide-react";
import Header from "./ui/Header";
import Footer from "./ui/Footer";
import { useAuth } from "../contexts/AuthContext";
import { useAuthService } from "../auth/sso/hooks/useAuthService";
import { useSAPCDC } from "../auth/sapcdc/hooks/useSAPCDC";
import devInstanceAccessService from "../services/DevInstanceAccessService";

// Capability categories shown on the hub. Only "Adapt Studio" is wired up for
// now (navigates to the existing instance-picker page) — the rest are
// COMING SOON placeholders matching the approved Figma design.
const CATEGORIES = [
  {
    title: "Content Creation",
    tagline: "Build new learning experiences",
    accent: "border-brand-blue",
    tools: [
      { name: "Adapt Studio", desc: "Course authoring", icon: PenSquare, action: "studio" },
      { name: "H5P", desc: "Interactive learning activities", icon: MonitorPlay, action: "h5p", url: "https://laerdal.h5p.com/" },
    ],
  },
  {
    title: "Content Assets",
    tagline: "Manage and reuse content",
    accent: "border-amber-500",
    tools: [
      { name: "DAMS", desc: "Digital asset management", icon: ImageIcon, action: "dams" },
      { name: "Assessment Hub", desc: "Assessments and knowledge checks", icon: ClipboardCheck },
    ],
  },
  {
    title: "Content Intelligence",
    tagline: "Understand how content performs",
    accent: "border-green-600",
    tools: [
      { name: "Course Insight Dashboard", desc: "Course-level analytics", icon: LayoutDashboard },
      { name: "Engagement Analytics", desc: "Learner behaviour and engagement insights", icon: PieChart },
    ],
  },
  {
    title: "Delivery",
    tagline: "Publish and consume learning",
    accent: "border-red-500",
    tools: [
      { name: "Knowledge Hub", desc: "Central learning destination", icon: Book },
    ],
  },
];

// DAMS base URL for the centralized-auth hand-off. Override per-env with
// REACT_APP_DAMS_URL; the hub launches DAMS the same way as an Adapt instance
// (redirect to <base>/api/user/email/<email>).
const DAMS_URL = (process.env.REACT_APP_DAMS_URL || "https://dams.laerdal.io").replace(/\/$/, "");

// "New Updates" and "Creator's Corner" are content-managed: their data lives
// in public/updates.json and public/creators.json (static assets, fetched at
// runtime) so editing those files updates the page without a rebuild.
const SIDEBAR_VISIBLE_COUNT = 3;

const UPDATE_TYPE_BADGE_CLASSES = {
  "New Feature": "bg-blue-50 text-blue-700 border-blue-200",
  Fix: "bg-amber-50 text-amber-700 border-amber-300",
};
const updateTypeBadgeClasses = (type) => UPDATE_TYPE_BADGE_CLASSES[type] || "bg-gray-100 text-gray-700 border-gray-300";

const AdaptStudioHub = () => {
  const navigate = useNavigate();
  const { getCurrentUser, logout: contextLogout } = useAuth();
  const { logout: ssoLogout } = useAuthService();
  const { logout: sapCDCLogout } = useSAPCDC();
  const { user, email, loginType } = getCurrentUser();

  // Launch DAMS via the same centralized-auth hand-off as an Adapt instance:
  // the authenticated email is passed; DAMS verifies + mints its own session.
  const launchDams = () => {
    const userEmail = email || user;
    if (userEmail) window.location.href = `${DAMS_URL}/api/user/email/${encodeURIComponent(userEmail)}`;
  };

  // Launch H5P externally.
  const launchH5P = () => {
    window.location.href = "https://laerdal.h5p.com/";
  };

  const [updates, setUpdates] = useState([]);
  const [creators, setCreators] = useState([]);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [showAllCreators, setShowAllCreators] = useState(false);
  const [hubAccessChecked, setHubAccessChecked] = useState(false);

  // Beta gate: direct URL access to /home is only for users allowlisted in
  // config.json's hubBetaAccess block — everyone else is sent to the
  // existing instance picker. Rendering is held back until this resolves so
  // an unauthorized user never sees a flash of the hub content.
  useEffect(() => {
    const userEmail = email || user;
    if (!userEmail) return;
    devInstanceAccessService.canAccessHubPage(userEmail).then((hasAccess) => {
      if (hasAccess) {
        setHubAccessChecked(true);
      } else {
        navigate("/instances", { replace: true });
      }
    });
  }, [email, user, navigate]);

  useEffect(() => {
    fetch("/updates.json", { cache: "no-store" })
      .then((response) => response.json())
      .then(setUpdates)
      .catch((error) => console.error("Error loading updates.json:", error));
    fetch("/creators.json", { cache: "no-store" })
      .then((response) => response.json())
      .then(setCreators)
      .catch((error) => console.error("Error loading creators.json:", error));
  }, []);

  const handleLogout = async () => {
    try {
      if (loginType?.includes("Azure AD") || loginType?.includes("SSO")) {
        contextLogout();
        await ssoLogout();
      } else {
        contextLogout();
        await sapCDCLogout();
        navigate("/");
      }
    } catch (error) {
      console.error("❌ [Adapt Studio Hub] Logout error:", error);
      contextLogout();
      navigate("/");
    }
  };

  if (!hubAccessChecked) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "18px", color: "#666" }}>
        Loading...
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-white flex flex-col justify-between">
      <Header user={user} onLogout={handleLogout} showUserDropdown={true} title="Unified Creator Platform" />

      <div className="flex-1 flex flex-col">
        {/* Hero */}
        <div className="bg-brand-blue px-8 py-10">
          <h1 className="text-white text-3xl font-bold mb-2">Everything you need, in one place</h1>
          <p className="text-primary-100 text-sm">
            Choose a capability to get started. Content creation, assets, transformation, intelligence, and delivery all live under the same umbrella.
          </p>
        </div>

        {/* Categories + sidebar */}
        <div className="flex-1 bg-gray-50 px-8 py-8 flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0 flex flex-col gap-10">
            {CATEGORIES.map((category) => (
              <section key={category.title}>
                <div className={`pl-3 mb-4 border-l-4 ${category.accent}`}>
                  <h2 className="text-xl font-bold text-gray-900">{category.title}</h2>
                  <p className="text-sm italic text-gray-500">"{category.tagline}"</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {category.tools.map((tool) => {
                    const Icon = tool.icon;
                    const isStudio = tool.action === "studio";
                    const isDams = tool.action === "dams";
                    const isH5P = tool.action === "h5p";
                    const wired = isStudio || isDams || isH5P;
                    return (
                      <div
                        key={tool.name}
                        className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-3 shadow-sm"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50">
                          <Icon className="w-5 h-5 text-brand-blue" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{tool.name}</p>
                          <p className="text-sm text-gray-500">{tool.desc}</p>
                        </div>
                        {wired ? (
                          <button
                            type="button"
                            onClick={
                              isStudio ? () => navigate("/instances")
                              : isDams ? launchDams
                              : isH5P ? launchH5P
                              : null
                            }
                            className="mt-auto flex items-center gap-1 text-sm font-semibold text-brand-blue hover:text-brand-darkBlue"
                          >
                            Open <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <span className="mt-auto text-xs font-bold tracking-wide text-gray-400">COMING SOON</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* Sidebar — content-managed via public/updates.json + public/creators.json */}
          <aside className="w-full lg:w-[340px] flex-shrink-0 flex flex-col gap-8">
            <section className="bg-amber-50 border border-amber-100 rounded-xl p-5">
              <div className="pl-3 mb-4 border-l-4 border-amber-500">
                <h2 className="text-lg font-bold text-gray-900">New Updates</h2>
                <p className="text-sm italic text-gray-500">Latest features and improvements from the Adapt team</p>
              </div>
              <div className={`flex flex-col gap-3 ${showAllUpdates ? "max-h-[420px] overflow-y-auto pr-1" : ""}`}>
                {(showAllUpdates ? updates : updates.slice(0, SIDEBAR_VISIBLE_COUNT)).map((update, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${updateTypeBadgeClasses(update.type)}`}>
                        {update.type}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{update.date}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{update.title}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">{update.description}</p>
                  </div>
                ))}
                {updates.length === 0 && <p className="text-sm text-gray-400">No updates yet.</p>}
              </div>
              {updates.length > SIDEBAR_VISIBLE_COUNT && (
                <button
                  type="button"
                  onClick={() => setShowAllUpdates((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm font-semibold text-brand-blue hover:text-brand-darkBlue"
                >
                  {showAllUpdates ? "Show less" : "View all updates"}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAllUpdates ? "rotate-180" : ""}`} />
                </button>
              )}
            </section>

            <section className="bg-green-50 border border-green-100 rounded-xl p-5">
              <div className="pl-3 mb-4 border-l-4 border-green-600">
                <h2 className="text-lg font-bold text-gray-900">Creator's Corner</h2>
                <p className="text-sm italic text-gray-500">What creators are saying</p>
              </div>
              <div className={`flex flex-col gap-3 ${showAllCreators ? "max-h-[420px] overflow-y-auto pr-1" : ""}`}>
                {(showAllCreators ? creators : creators.slice(0, SIDEBAR_VISIBLE_COUNT)).map((creator, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm flex flex-col gap-2">
                    <p className="text-sm italic text-gray-700 leading-relaxed">"{creator.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-green-100 border border-green-300 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-green-700">{creator.initials}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{creator.name}</p>
                        <p className="text-xs text-gray-500">{creator.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {creators.length === 0 && <p className="text-sm text-gray-400">No testimonials yet.</p>}
              </div>
              {creators.length > SIDEBAR_VISIBLE_COUNT && (
                <button
                  type="button"
                  onClick={() => setShowAllCreators((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm font-semibold text-brand-blue hover:text-brand-darkBlue"
                >
                  {showAllCreators ? "Show less" : "View all testimonials"}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAllCreators ? "rotate-180" : ""}`} />
                </button>
              )}
            </section>
          </aside>
        </div>
      </div>

      <Footer />
    </section>
  );
};

export default AdaptStudioHub;
